// IMPORTS
import React, { useEffect, useState } from 'react';
import { Button, Checkbox, FormControlLabel, Grid2, Menu, MenuItem, Popover, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { DateCalendar } from '@mui/x-date-pickers';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { TimePickerHour, TimePickerMinutes } from '../Input/index';
import { DataGridProps, OptionsMultiSelect, ButtonCell, ButtonCellType, DatePickerCell, DatePickerCellType, MultiSelectCell, MultiSelectCellType, ValuesOptions, CustomCellType, CustomCell } from './types';
import { CellTemplate, Compatible, getCellProperty, ReactGrid, Uncertain } from '@silevis/reactgrid';
import '@silevis/reactgrid/styles.css';
import { useTranslation } from 'react-i18next';

// CUSTOM RENDER CELLS
const ButtonCell = ({ text, onClick, style, icon }: ButtonCellType) => (
  <Button onClick={onClick} sx={{ width: '100%', height: '100%', ...style }}>
    {icon ? <DeleteIcon /> : text}
  </Button>
);

const MultiSelectCell = ({ options, onSelect, selectedOptions }: MultiSelectCellType) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { t: translate } = useTranslation();

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOptionChange = (optionId: number, isSelected: boolean) => {
    if (onSelect) {
      onSelect(optionId, isSelected);
    }
  };

  let selectedOptionsLabel;

  if (selectedOptions) {
    selectedOptionsLabel =
      options
        .filter((option) => selectedOptions.includes(option.id))
        .map((option) => option.label)
        .join(', ') || translate("coreUi.data_grid.select");
  }

  return (
    <>
      <Button onClick={handleClick} sx={{ width: '100%', height: '100%' }}>
        {selectedOptionsLabel}
      </Button>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {options.map((option) => (
          <MenuItem key={option.id}>
            <FormControlLabel control={<Checkbox checked={selectedOptions ? selectedOptions.includes(option.id) : false} onChange={(e) => handleOptionChange(option.id, e.target.checked)} />} label={option.label} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const DatePickerCell = ({ value, label, handleDateChange, exp }: DatePickerCellType) => {
  const valueDate = new Date(value);
  const [hour, setHour] = useState<number | string | undefined>(value ? valueDate.getHours() : 12);
  const [minute, setMinute] = useState<number | string | undefined>(value ? valueDate.getMinutes() : '00');
  const [anchorEl, setAnchorEl] = useState(null);
  const [valueParsed, setValueParsed] = useState<Date | string>(value);

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const handleTimeChange = (newDate: string) => {
    if (!Date.parse(newDate)) {
      return;
    }
    setValueParsed(new Date(newDate));
  };

  useEffect(() => {
    if (!valueParsed) {
      return;
    }

    let date: Date;

    if (typeof valueParsed === 'string') {
      const parsedDate = new Date(valueParsed);
      if (isNaN(parsedDate.getTime())) {
        console.error('valueParsed no es una fecha válida');
        return;
      }
      date = parsedDate;
    } else if (valueParsed instanceof Date) {
      date = valueParsed;
    } else {
      console.error('valueParsed tiene un tipo inválido');
      return;
    }

    date.setHours(hour as number);
    date.setMinutes(minute as number);
    handleDateChange(date, exp?.rowId);
  }, [valueParsed, hour, minute]);

  return (
    <Grid2 container display="flex" justifyContent="center" alignItems="center" sx={{ width: '100%', height: '100%' }}>
      <Grid2 sx={{ width: '100%', height: '100%' }} display="flex" justifyContent={value !== undefined ? 'center' : 'flex-start'} alignItems="center" spacing={2}>
        {value !== undefined && (
          <Typography
            variant="subtitle1"
            sx={{
              fontSize: '13px',
              justifyContent: 'flex-start',
              fontWeight: 400,
            }}
            gutterBottom>
            {String(label)}
          </Typography>
        )}
        <Button
          onClick={handleClick}
          sx={{
            width: '40px',
            height: '40px',
            padding: '8px',
            borderRadius: '50%',
            minWidth: '0px',
          }}>
          <CalendarTodayIcon fontSize="small" />
        </Button>
      </Grid2>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}>
        <DateCalendar views={['year', 'month', 'day']} value={value ? (valueParsed as Date) : null} onChange={handleTimeChange} />
        <Grid2 padding={2} display="flex" spacing={1} justifyContent="center" alignItems="center" borderTop="1px solid #ccc" width="100%">
          <TimePickerHour
            id={'datePickerHour'}
            label="Hora"
            value={hour || 0}
            onFieldChange={(selected: ValuesOptions) => {
              setHour(selected.id);
            }}
          />
          <TimePickerMinutes
            id={'datePickerMinutes'}
            label="Minutos"
            value={minute || 0}
            onFieldChange={(selected: ValuesOptions) => {
              setMinute(selected.id);
            }}
          />
        </Grid2>
      </Popover>
    </Grid2>
  );
};

const CustomCell = ({ children }: CustomCellType) => {
  return children;
};

const DataGrid: React.FC<DataGridProps> = ({ rows, columns, handleSelectedResource, handleChange, ...props }) => {
  // ESTADOS
  const [selectedOptions, setSelectedOptions] = useState([]);

  // FUNCIONES
  const handleSelectOption = (resourceId: string | number, categoryId: number, isSelected: boolean) => {
    setSelectedOptions((prev: any) => {
      const newSelectedOptions = isSelected
        ? [...prev, resourceId]
        : prev.filter((id: number) => {
            return id !== resourceId;
          });

      if (handleSelectedResource) {
        handleSelectedResource(resourceId as number, categoryId, isSelected);
      }
      return newSelectedOptions;
    });
  };

  // CLASES

  class CustomCellTemplate implements CellTemplate<CustomCell> {
    getCompatibleCell(uncertainCell: Uncertain<CustomCell>): Compatible<CustomCell> {
      const children = getCellProperty(uncertainCell, 'children', 'object');
      return {
        ...uncertainCell,
        children,
        value: 0,
        text: '',
      };
    }

    render(cell: Compatible<CustomCell>): React.ReactNode {
      return <CustomCell children={cell.children} />;
    }
  }

  class ButtonCellTemplate implements CellTemplate<ButtonCell> {
    getCompatibleCell(uncertainCell: Uncertain<ButtonCell>): Compatible<ButtonCell> {
      const text = getCellProperty(uncertainCell, 'text', 'string');
      const icon = uncertainCell.icon ?? false;
      const exp = getCellProperty(uncertainCell, 'exp', 'object');
      const handleClick = getCellProperty(uncertainCell, 'handleClick', 'function');

      return {
        ...uncertainCell,
        text,
        value: 0, // ESTO ESTA ASI PARA QUE TS NO MOLESTE, PERO NO AFECTA EN NADA.
        icon,
        exp,
        handleClick,
      };
    }

    render(cell: Compatible<ButtonCell>): React.ReactNode {
      return (
        <ButtonCell
          text={cell.text}
          onClick={() => {
            cell.handleClick(cell.exp);
          }}
          icon={cell.icon || false}
        />
      );
    }
  }

  class MultiSelectCellTemplate implements CellTemplate<MultiSelectCell> {
    getCompatibleCell(uncertainCell: Uncertain<MultiSelectCell>): Compatible<MultiSelectCell> {
      const options = getCellProperty(uncertainCell, 'options', 'object') as OptionsMultiSelect[];
      const selectedOptions = uncertainCell.selectedOptions ?? [];
      const categoryId = getCellProperty(uncertainCell, 'categoryId', 'number');
      const onSelect = uncertainCell.onSelect ?? undefined;
      return {
        ...uncertainCell,
        text: '',
        value: 0,
        options,
        selectedOptions,
        categoryId,
        onSelect,
      };
    }
    render(cell: Compatible<MultiSelectCell>): React.ReactNode {
      return <MultiSelectCell options={cell.options} selectedOptions={selectedOptions} onSelect={(resourceId, isSelected) => handleSelectOption(resourceId, cell.categoryId, isSelected)} />;
    }
  }

  class DatePickerCellTemplate implements CellTemplate<DatePickerCell> {
    getCompatibleCell(uncertainCell: Uncertain<DatePickerCell>): Compatible<DatePickerCell> {
      const value = getCellProperty(uncertainCell, 'value', 'string');
      const label = getCellProperty(uncertainCell, 'label', 'string');
      const handleDateChange = getCellProperty(uncertainCell, 'handleDateChange', 'function');
      const exp = getCellProperty(uncertainCell, 'exp', 'object');
      return {
        ...uncertainCell,
        text: '',
        value,
        label,
        handleDateChange,
        exp,
      };
    }

    render(cell: Compatible<DatePickerCell>): React.ReactNode {
      return <DatePickerCell value={cell.value} label={cell.label} handleDateChange={cell.handleDateChange} exp={cell.exp} {...props} />;
    }
  }

  // CUSTOM CELLS TEMPLATES
  const customCellTemplates = {
    button: new ButtonCellTemplate(),
    multiSelect: new MultiSelectCellTemplate(),
    datePicker: new DatePickerCellTemplate(),
    customCell: new CustomCellTemplate(),
  };

  // RETUNR
  return <ReactGrid rows={rows} columns={columns} customCellTemplates={customCellTemplates} onCellsChanged={handleChange} {...props} />;
};

export default DataGrid;
